import type { FormEvent } from 'react'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '@/context/AuthContext'
import { errorMessage, fieldErrors } from '@/lib/api'
import Button from '@/components/ui/Button'
import { Field, Input, PasswordInput } from '@/components/ui/Field'
import { useToast } from '@/components/ui/toast'

export default function Register() {
  const { register } = useAuth()
  const toast = useToast()
  const navigate = useNavigate()
  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    password_confirmation: '',
  })
  const [errors, setErrors] = useState<Record<string, string[]>>({})
  const [loading, setLoading] = useState(false)

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setErrors({})
    try {
      await register(form)
      toast.success('Account ready!', 'Welcome to MPG Printing - you can start ordering right away.')
      navigate('/account')
    } catch (err) {
      setErrors(fieldErrors(err))
      toast.error('Registration failed', errorMessage(err))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-primary bg-noise xl:bg-background xl:bg-none">
      <div className="absolute -left-[26%] -top-[30%] hidden h-[160%] w-[76%] rotate-[6deg] rounded-[35%] border-[20px] border-primary/20 bg-primary/95 bg-noise xl:block" />

      <div className="relative z-10 mx-auto flex min-h-screen max-w-6xl items-center px-5">
        <div className="hidden w-1/2 flex-col pr-16 xl:flex">
          <div className="flex items-center">
            <Link to="/" title="MPG marketplace">
              <img src="/logo_hd_white.png" alt="MPG Printing" className="h-8 w-auto object-contain" />
            </Link>
          </div>
          <img src="/auth-illustration.webp" alt="" className="mt-14 w-3/4" />
          <h1 className="mt-10 text-4xl font-medium leading-tight text-white">
            A few more clicks to <br /> create your account.
          </h1>
          <p className="mt-4 text-lg text-white opacity-60">Sign up and start ordering right away</p>
        </div>

        <div className="flex w-full justify-center py-10 xl:w-1/2 xl:justify-end">
          {(
            <div className="box w-full max-w-md p-8 xl:border-0 xl:bg-transparent xl:shadow-none xl:before:hidden">
              <h2 className="text-center text-2xl font-semibold text-foreground xl:text-left xl:text-3xl">Sign Up</h2>
              <p className="mt-2 text-center text-sm text-foreground/60 xl:text-left">
                Create your customer account - start ordering immediately
              </p>

              <form onSubmit={submit} className="mt-7 space-y-4">
                <Field error={errors.name?.[0]}>
                  <Input placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
                </Field>
                <Field error={errors.email?.[0]}>
                  <Input type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
                </Field>
                <Field error={errors.password?.[0]}>
                  <PasswordInput placeholder="Password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required />
                </Field>
                <Field>
                  <PasswordInput
                    placeholder="Password confirmation"
                    value={form.password_confirmation}
                    onChange={(e) => setForm({ ...form, password_confirmation: e.target.value })}
                    required
                  />
                </Field>
                <div className="grid grid-cols-2 gap-3 pt-2">
                  <Button type="submit" loading={loading}>
                    Register
                  </Button>
                  <Link to="/login">
                    <Button variant="outline" className="w-full">
                      Sign in
                    </Button>
                  </Link>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
